import React from "react";
import {
  Box,
  Button,
  Dialog,
  Divider,
  IconButton,
  Stack,
  Typography,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import CloseIcon from "@mui/icons-material/Close";

export function focusRequiredField(fieldId) {
  window.requestAnimationFrame(() => {
    const el = document.getElementById(fieldId);
    if (!el) return;
    el.scrollIntoView({ behavior: "smooth", block: "center" });
    const focusable = el.matches("input, select, textarea, [role='combobox']")
      ? el
      : el.querySelector("input, [role='combobox']");
    focusable?.focus?.();
  });
}

export default function RequiredChoiceDialog({
  open,
  onClose,
  onAction,
  eyebrow = "Save",
  title,
  message,
  actionLabel = "Choose",
  zIndex = 8000,
}) {
  const theme = useTheme();

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xs"
      fullWidth
      sx={{ zIndex }}
      ModalProps={{ sx: { zIndex } }}
      PaperProps={{
        sx: {
          borderRadius: "22px",
          mx: { xs: 2, sm: 0 },
          bgcolor: "background.paper",
          boxShadow: theme.palette.mode === "dark"
            ? "0 28px 70px rgba(0,0,0,0.52)"
            : "0 28px 70px rgba(15,23,42,0.24)",
        },
      }}
      slotProps={{
        backdrop: {
          sx: {
            bgcolor: "rgba(15,23,42,0.38)",
            backdropFilter: "blur(2px)",
          },
        },
      }}
    >
      <Box sx={{ px: { xs: 2.5, sm: 3.25 }, pt: 2.5, pb: 2 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="flex-start" gap={2}>
          <Box sx={{ minWidth: 0 }}>
            <Typography
              sx={{
                fontSize: "0.68rem",
                fontWeight: 700,
                color: "text.secondary",
                letterSpacing: "0.08em",
                textTransform: "uppercase",
                mb: 0.75,
              }}
            >
              {eyebrow}
            </Typography>
            <Typography sx={{ fontSize: { xs: "1.2rem", sm: "1.28rem" }, fontWeight: 700, lineHeight: 1.25 }}>
              {title}
            </Typography>
            <Typography sx={{ mt: 0.9, fontSize: "0.86rem", color: "text.secondary", lineHeight: 1.55 }}>
              {message}
            </Typography>
          </Box>
          <IconButton
            onClick={onClose}
            aria-label="Close"
            size="small"
            sx={{
              width: 40,
              height: 40,
              borderRadius: "12px",
              bgcolor: "action.hover",
              color: "text.secondary",
              flexShrink: 0,
            }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </Stack>
      </Box>
      <Divider />
      <Stack
        direction={{ xs: "column-reverse", sm: "row" }}
        justifyContent="flex-end"
        spacing={1.25}
        sx={{ px: { xs: 2.5, sm: 3.25 }, py: 2 }}
      >
        <Button
          onClick={onClose}
          sx={{
            textTransform: "none",
            fontWeight: 600,
            color: "text.secondary",
            bgcolor: "action.hover",
            borderRadius: "12px",
            px: 2,
            minHeight: 44,
          }}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={onAction}
          sx={{
            textTransform: "none",
            fontWeight: 700,
            borderRadius: "12px",
            px: 2,
            minHeight: 44,
            bgcolor: "#0874f9",
            boxShadow: "none",
            "&:hover": { bgcolor: "#0667df", boxShadow: "none" },
          }}
        >
          {actionLabel}
        </Button>
      </Stack>
    </Dialog>
  );
}
